/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Client-side mirror of `creditableEntries`
 * (`functions/src/transitions/paymentGate.ts`) — the single definition of
 * "which ledger entries count as money received against this quote".
 *
 * A quarantined entry is money the gateway really captured against a quote that
 * could not accept it (superseded / lost / parked / already fully paid). It is
 * recorded for visibility and audit, moves no status, and **must be excluded
 * from every figure that means "what has been credited"** — the customer's
 * balance, the deposit gate, the promotion decision, the minimum first payment.
 * There is deliberately NO way to credit it: the only safe remediation is to
 * refund in Airwallex and then remove the entry via the admin ledger
 * adjustment (`adminAdjustLedger` / the Payments-view Adjust action).
 *
 * ── WHY THIS IS A COPY AND NOT AN IMPORT ────────────────────────────────────
 * `shared/` is imported by both halves, but the server's own `paymentGate.ts`
 * policy stays server-side — it is not shared code.
 * The two remaining options were (a) relocate the predicate into `src/` and
 * share it back, or (b) this deliberate one-line duplicate.
 *
 * (b) was chosen because:
 *   - `paymentGate.ts` is server POLICY (which statuses are payable, what a
 *     gateway payment on a dead status does). Vendoring the whole file would
 *     ship that policy into the customer bundle; splitting out one predicate
 *     would relocate a module every task in this slice imports.
 *   - A move in or out of `shared/` has to update
 *     ci.yml's `functions:` paths filter in lockstep; Lesson #58 records that
 *     skew silently skipping a functions deploy.
 *   - The rule is `!e.quarantined` — a boolean-field negation, not arithmetic.
 *     Vendoring exists to stop FORMULAS drifting (`depositGate`'s ratio +
 *     cent-precision compare, `scheduling`'s working-day calendar).
 *     There is no version of this predicate that can drift meaningfully.
 *
 * **If you change the meaning of `quarantined` on either side, change both.**
 * Server: `functions/src/transitions/paymentGate.ts` → `creditableEntries`.
 * Client: this file. Nothing else in `src/` may hand-roll the filter.
 *
 * Since backlog #61 this file IS also shared with the Cloud Functions — not to
 * replace `creditableEntries` (which stays canonical for Cloud Function policy
 * code), but so other shared pure rules (`scheduling`)
 * can call the filter without hand-rolling it. The reasoning above still holds:
 * the server's own paymentGate policy is never pulled into src/.
 *
 * NOT for surfaces that mean "money that physically arrived" — audit trails,
 * the ledger modal's row list, gateway event records. Quarantined money DID
 * arrive; the invariant is about *credited*, not *received*.
 */

/** Drop quarantined entries. Structural type so both the client
 *  `PaymentLedgerEntry` and any narrower row shape satisfy it. */
export const creditableLedgerEntries = <T extends { quarantined?: boolean }>(
  ledger: ReadonlyArray<T> | undefined | null,
): T[] => (ledger ?? []).filter((e) => !e.quarantined);
